import { skipToken } from '@reduxjs/toolkit/query';

import { api } from './index';
import { momentsBearerQuery } from './momentsApi';

import type {
  AppointmentKind,
  ParticipantAppointmentDetail,
  ParticipantAppointmentMutationResult,
  ParticipantAppointmentOperation,
  ParticipantCoachCancelAppointmentCommand,
  ParticipantMemberCancelAppointmentCommand,
  ParticipantSchedulingContext,
} from '@/features/appointments/types';

import { appointmentContractV1 } from '@/features/appointments/contractCodec';

// Moments participant eligibility still requires this flag for the direct
// transport, and nothing else in the frontend reads it. Remove it together with
// that server check (retire-new-calendars step 4); until then routing on the
// direct-flow flag alone would send members to an endpoint that refuses them.
const NEW_CALENDARS_FLAG_KEY = 'NEW_CALENDARS';

export const DIRECT_PARTICIPANT_APPOINTMENT_BOOKING_FLAG_KEY =
  'DIRECT_PARTICIPANT_APPOINTMENT_BOOKING';
export const DIRECT_PARTICIPANT_APPOINTMENT_CANCELS_FLAG_KEY =
  'DIRECT_PARTICIPANT_APPOINTMENT_CANCELS';

export type ParticipantAppointmentFlow = 'booking' | 'cancellation';
export type ParticipantAppointmentTransport = 'moments' | 'rails';

interface ParticipantFlagEvaluation {
  enabled?: boolean;
  isError?: boolean;
  isLoading?: boolean;
}

export type ParticipantAppointmentRouteDecision =
  | { state: 'loading' }
  | { state: 'unavailable' }
  | {
      retained: boolean;
      state: 'ready';
      transport: ParticipantAppointmentTransport;
    };

export const participantAppointmentRouteDecision = ({
  directFlow,
  establishedTransport,
  newCalendars,
}: {
  directFlow: ParticipantFlagEvaluation;
  establishedTransport?: ParticipantAppointmentTransport;
  newCalendars: ParticipantFlagEvaluation;
}): ParticipantAppointmentRouteDecision => {
  if (establishedTransport) {
    return {
      retained: true,
      state: 'ready',
      transport: establishedTransport,
    };
  }
  if (newCalendars.isLoading || directFlow.isLoading) {
    return { state: 'loading' };
  }
  if (newCalendars.isError || directFlow.isError) {
    return { state: 'unavailable' };
  }
  if (
    typeof newCalendars.enabled !== 'boolean' ||
    typeof directFlow.enabled !== 'boolean'
  ) {
    return { state: 'unavailable' };
  }
  return {
    retained: false,
    state: 'ready',
    transport: newCalendars.enabled && directFlow.enabled ? 'moments' : 'rails',
  };
};

export interface MomentsParticipantCounterpartSelector {
  externalId: string;
  provider: string;
}

export interface BookMomentsParticipantAppointmentRequest {
  bookingPattern: 'one_off' | 'series';
  calendarConflictOverride?: boolean;
  counterpart: MomentsParticipantCounterpartSelector;
  idempotencyKey: string;
  kind: AppointmentKind;
  startAt: string;
  viewerTimezone: string;
}

export interface GetMomentsParticipantSchedulingContextRequest {
  bookingPattern?: 'one_off';
  counterpart: MomentsParticipantCounterpartSelector;
  kind: AppointmentKind;
  viewerTimezone: string;
  visibleWeeks: number;
}

export interface GetMomentsParticipantAppointmentRequest {
  appointmentId: string;
}

type ParticipantCancelRequest<TCommand> = TCommand extends unknown
  ? Omit<TCommand, 'contractVersion'> & { appointmentId: string }
  : never;

export type CancelMomentsParticipantAppointmentAsMemberRequest =
  ParticipantCancelRequest<ParticipantMemberCancelAppointmentCommand>;

export type CancelMomentsParticipantAppointmentAsCoachRequest =
  ParticipantCancelRequest<ParticipantCoachCancelAppointmentCommand>;

export interface GetMomentsParticipantAppointmentOperationRequest {
  operationId: string;
}

export interface GetMomentsParticipantAppointmentFlagForCoachRequest {
  coachExternalId: string;
  flagKey:
    | typeof NEW_CALENDARS_FLAG_KEY
    | typeof DIRECT_PARTICIPANT_APPOINTMENT_BOOKING_FLAG_KEY
    | typeof DIRECT_PARTICIPANT_APPOINTMENT_CANCELS_FLAG_KEY;
}

export interface MomentsParticipantAppointmentFlagForCoach {
  enabled: boolean;
  flagKey: string;
}

type ParticipantInvalidationTag =
  'Session' | { id: string; type: 'Appointment' };

const participantTerminalInvalidationTags = (
  result:
    | ParticipantAppointmentMutationResult
    | ParticipantAppointmentOperation
    | undefined,
  appointmentIds: string[] = [],
): ParticipantInvalidationTag[] => {
  if (!result || result.status === 'pending') return [];
  const ids = new Set([
    ...appointmentIds,
    ...result.appointments.map(({ id }) => id),
  ]);
  return [
    { id: 'LIST', type: 'Appointment' },
    { id: 'SCHEDULING_CONTEXT', type: 'Appointment' },
    { id: 'PARTICIPANT_SCHEDULING_CONTEXT', type: 'Appointment' },
    ...Array.from(ids, (id) => ({ id, type: 'Appointment' as const })),
    ...(result.status === 'confirmed' ||
    result.status === 'needs_reconciliation'
      ? (['Session'] as const)
      : []),
  ];
};

const counterpartQuery = (
  counterpart: MomentsParticipantCounterpartSelector,
): string =>
  new URLSearchParams({
    counterpart_external_id: counterpart.externalId,
    counterpart_provider: counterpart.provider,
  }).toString();

const decodeParticipantAppointmentFlag = (
  payload: unknown,
  expectedFlagKey: string,
): MomentsParticipantAppointmentFlagForCoach => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Invalid participant Appointment flag response');
  }
  const response = payload as Record<string, unknown>;
  if (
    response.flag_key !== expectedFlagKey ||
    typeof response.enabled !== 'boolean' ||
    response.source !== 'moments'
  ) {
    throw new Error('Invalid participant Appointment flag response');
  }
  return { enabled: response.enabled, flagKey: expectedFlagKey };
};

export const momentsParticipantAppointmentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    bookMomentsParticipantAppointment: builder.mutation<
      ParticipantAppointmentMutationResult,
      BookMomentsParticipantAppointmentRequest
    >({
      invalidatesTags: (result) => participantTerminalInvalidationTags(result),
      queryFn: async ({ counterpart, ...request }, queryApi) =>
        momentsBearerQuery(queryApi, {
          body: appointmentContractV1.participantBookCommand.encode({
            ...request,
            contractVersion: 'appointment-participant-book.v1',
          }),
          fallbackData: null as unknown as ParticipantAppointmentMutationResult,
          mapResponse: (payload) =>
            appointmentContractV1.participantMutationResult.decode(payload),
          method: 'POST',
          path: `/api/v1/appointments?${counterpartQuery(counterpart)}`,
          returnErrorOnFailure: true,
        }),
    }),
    cancelMomentsParticipantAppointmentAsCoach: builder.mutation<
      ParticipantAppointmentMutationResult,
      CancelMomentsParticipantAppointmentAsCoachRequest
    >({
      invalidatesTags: (result, _error, { appointmentId }) =>
        participantTerminalInvalidationTags(result, [appointmentId]),
      queryFn: async ({ appointmentId, ...request }, queryApi) =>
        momentsBearerQuery(queryApi, {
          body: appointmentContractV1.participantCoachCancelCommand.encode({
            ...request,
            contractVersion: 'appointment-participant-coach-cancel.v1',
          }),
          fallbackData: null as unknown as ParticipantAppointmentMutationResult,
          mapResponse: (payload) =>
            appointmentContractV1.participantMutationResult.decode(payload),
          method: 'POST',
          path: `/api/v1/appointments/${encodeURIComponent(appointmentId)}/cancel`,
          returnErrorOnFailure: true,
        }),
    }),
    cancelMomentsParticipantAppointmentAsMember: builder.mutation<
      ParticipantAppointmentMutationResult,
      CancelMomentsParticipantAppointmentAsMemberRequest
    >({
      invalidatesTags: (result, _error, { appointmentId }) =>
        participantTerminalInvalidationTags(result, [appointmentId]),
      queryFn: async ({ appointmentId, ...request }, queryApi) =>
        momentsBearerQuery(queryApi, {
          body: appointmentContractV1.participantMemberCancelCommand.encode({
            ...request,
            contractVersion: 'appointment-participant-member-cancel.v1',
          }),
          fallbackData: null as unknown as ParticipantAppointmentMutationResult,
          mapResponse: (payload) =>
            appointmentContractV1.participantMutationResult.decode(payload),
          method: 'POST',
          path: `/api/v1/appointments/${encodeURIComponent(appointmentId)}/cancel`,
          returnErrorOnFailure: true,
        }),
    }),
    getMomentsParticipantAppointment: builder.query<
      ParticipantAppointmentDetail,
      GetMomentsParticipantAppointmentRequest
    >({
      providesTags: (_result, _error, { appointmentId }) => [
        { id: appointmentId, type: 'Appointment' },
      ],
      queryFn: async ({ appointmentId }, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: null as unknown as ParticipantAppointmentDetail,
          mapResponse: (payload) => {
            const appointment =
              appointmentContractV1.participantAppointmentDetail.decode(
                payload,
              );
            if (appointment.id !== appointmentId) {
              throw new Error(
                'Participant Appointment detail identity mismatch',
              );
            }
            return appointment;
          },
          path: `/api/v1/appointments/${encodeURIComponent(appointmentId)}`,
          returnErrorOnFailure: true,
        }),
    }),
    getMomentsParticipantAppointmentFlagForCoach: builder.query<
      MomentsParticipantAppointmentFlagForCoach,
      GetMomentsParticipantAppointmentFlagForCoachRequest
    >({
      providesTags: ['MomentsFeatureFlags'],
      queryFn: async ({ coachExternalId, flagKey }, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData:
            null as unknown as MomentsParticipantAppointmentFlagForCoach,
          mapResponse: (payload) =>
            decodeParticipantAppointmentFlag(payload, flagKey),
          path: `/api/v1/feature-flags/subjects/by-identity?${new URLSearchParams(
            {
              external_id: coachExternalId,
              flag_key: flagKey,
              provider: 'legacy_app',
            },
          ).toString()}`,
          returnErrorOnFailure: true,
        }),
    }),
    getMomentsParticipantAppointmentOperation: builder.query<
      ParticipantAppointmentOperation,
      GetMomentsParticipantAppointmentOperationRequest
    >({
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const tags = participantTerminalInvalidationTags(
            data,
            data.appointmentIds,
          );
          if (tags.length > 0) dispatch(api.util.invalidateTags(tags));
        } catch {
          // Pending and failed polls must not invalidate shared caches.
        }
      },
      providesTags: (_result, _error, { operationId }) => [
        { id: `participant-operation:${operationId}`, type: 'Appointment' },
      ],
      queryFn: async ({ operationId }, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: null as unknown as ParticipantAppointmentOperation,
          mapResponse: (payload) => {
            const operation =
              appointmentContractV1.participantOperation.decode(payload);
            if (operation.id !== operationId) {
              throw new Error(
                'Participant Appointment operation identity mismatch',
              );
            }
            return operation;
          },
          path: `/api/v1/appointment-operations/${encodeURIComponent(operationId)}`,
          returnErrorOnFailure: true,
        }),
    }),
    getMomentsParticipantSchedulingContext: builder.query<
      ParticipantSchedulingContext,
      GetMomentsParticipantSchedulingContextRequest
    >({
      providesTags: [
        { id: 'PARTICIPANT_SCHEDULING_CONTEXT', type: 'Appointment' },
      ],
      queryFn: async (
        { bookingPattern, counterpart, kind, viewerTimezone, visibleWeeks },
        queryApi,
      ) => {
        const query = new URLSearchParams({
          counterpart_external_id: counterpart.externalId,
          counterpart_provider: counterpart.provider,
          intent: 'book',
          kind,
          viewer_timezone: viewerTimezone,
          visible_weeks: String(visibleWeeks),
        });
        if (bookingPattern === 'one_off') {
          query.set('booking_pattern', bookingPattern);
        }
        return momentsBearerQuery(queryApi, {
          fallbackData: null as unknown as ParticipantSchedulingContext,
          mapResponse: (payload) =>
            appointmentContractV1.participantSchedulingContext.decode(payload),
          path: `/api/v1/appointments/scheduling-context?${query.toString()}`,
          returnErrorOnFailure: true,
        });
      },
    }),
  }),
});

export const {
  useBookMomentsParticipantAppointmentMutation,
  useCancelMomentsParticipantAppointmentAsCoachMutation,
  useCancelMomentsParticipantAppointmentAsMemberMutation,
  useGetMomentsParticipantAppointmentQuery,
  useGetMomentsParticipantAppointmentOperationQuery,
  useGetMomentsParticipantAppointmentFlagForCoachQuery,
  useLazyGetMomentsParticipantAppointmentOperationQuery,
  useGetMomentsParticipantSchedulingContextQuery,
  useLazyGetMomentsParticipantAppointmentQuery,
  useLazyGetMomentsParticipantSchedulingContextQuery,
} = momentsParticipantAppointmentApi;

export const useParticipantAppointmentRouteDecision = ({
  coachExternalId,
  establishedTransport,
  flow,
}: {
  coachExternalId: string | undefined;
  establishedTransport?: ParticipantAppointmentTransport;
  flow: ParticipantAppointmentFlow;
}): ParticipantAppointmentRouteDecision => {
  const newCalendars = useGetMomentsParticipantAppointmentFlagForCoachQuery(
    coachExternalId && !establishedTransport
      ? { coachExternalId, flagKey: NEW_CALENDARS_FLAG_KEY }
      : skipToken,
  );
  const directFlow = useGetMomentsParticipantAppointmentFlagForCoachQuery(
    coachExternalId && !establishedTransport
      ? {
          coachExternalId,
          flagKey:
            flow === 'booking'
              ? DIRECT_PARTICIPANT_APPOINTMENT_BOOKING_FLAG_KEY
              : DIRECT_PARTICIPANT_APPOINTMENT_CANCELS_FLAG_KEY,
        }
      : skipToken,
  );
  return participantAppointmentRouteDecision({
    directFlow: {
      enabled: directFlow.data?.enabled,
      isError: directFlow.isError,
      isLoading: directFlow.isLoading || directFlow.isFetching,
    },
    establishedTransport,
    newCalendars: {
      enabled: newCalendars.data?.enabled,
      isError: newCalendars.isError,
      isLoading: newCalendars.isLoading || newCalendars.isFetching,
    },
  });
};
